import React from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { get_t_str } from "../../../../helpers/helpers";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import {
  API_get_now_tasks,
  API_get_now_tasks_kiosk,
  API_start_plan_task_assignment,
  API_start_plan_task_assignment_kiosk,
  API_pause_plan_task_assignment,
  API_pause_plan_task_assignment_kiosk,
  API_resume_plan_task_assignment,
} from "../../../../constants";
import TaskNameCardInput from "./TaskNameCardInput";
import TaskNameCardModalHeader from "./TaskNameCardModalHeader";
import TaskNameCardCompletedTask from "./TaskNameCardCompletedTask";
import TaskPhotoPreview from "./TaskPhotoPreview";
import {
  durationLabel,
  elapsedClock,
  locallyDone,
  qhnumToDate,
  resumeStepIndex,
  workElapsedMs,
  workClockIsProvisional,
  othersWorkingNames,
  namesPhrase,
  joinPrompt,
  memberClocks,
} from "../tasks_hub/taskHubUtils";
import { useTaskComments, CommentList, CommentComposer } from "../tasks_hub/TaskConversation";

function TaskNameCardEmployeeModal(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(700px, calc(100vw - 32px))",
    maxHeight: "92vh",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 0,
    display: "flex",
    flexDirection: "column",
  };

  const [modalData, setModalData] = React.useState(props.task);
  const [previewImage, setPreviewImage] = React.useState(null);
  React.useEffect(() => setPreviewImage(null), [props.open, props.task?.uuid]);
  const setPreviewOpen = (open) => { if (!open) setPreviewImage(null); };

  // Kiosk mode: the modal acts for the person at the shared screen through
  // the *_kiosk twins, identified by their member id or, when the store
  // requires a PIN, by the PIN they typed. Same states, same buttons; only
  // the endpoints and the identity on each request differ. The thread goes
  // the same way, so a comment left at the kiosk is signed by that person.
  const kiosk = props.kiosk || null;
  const kioskParams = kiosk
    ? kiosk.clockPin
      ? { clock_pin: kiosk.clockPin }
      : { store_team_member_id: kiosk.storeTeamMemberId }
    : {};
  const lifecycleUrl = (kind) => {
    if (kiosk) {
      // Start also resumes a parked occurrence on the kiosk (one endpoint).
      return kind === "pause" ? API_pause_plan_task_assignment_kiosk : API_start_plan_task_assignment_kiosk;
    }
    if (kind === "pause") return API_pause_plan_task_assignment;
    if (kind === "resume") return API_resume_plan_task_assignment;
    return API_start_plan_task_assignment;
  };



  // Checklist / Comments. The thread lives behind a tab in the pinned header
  // so a manager's note is visible the moment the modal opens, not six photo
  // steps down, and the composer takes the footer the way it does on tickets.
  const [tab, setTab] = React.useState(props.initialTab || "checklist");
  React.useEffect(() => {
    setTab(props.initialTab || "checklist");
    setLockedHeight(null);
  }, [props.task?.uuid, props.initialTab]);
  // The box sizes to its content, so a short thread would shrink it. Lock the
  // height the checklist rendered at before switching, and the frame stays
  // put whichever tab is showing.
  const boxRef = React.useRef(null);
  const [lockedHeight, setLockedHeight] = React.useState(null);
  const switchTab = (key) => {
    if (boxRef.current && lockedHeight === null) setLockedHeight(boxRef.current.offsetHeight);
    setTab(key);
  };
  const {
    comments,
    loading: commentsLoading,
    posting,
    post,
  } = useTaskComments({
    storeId: modalData?.store_id,
    // Fetched only once the tab is open; the row count covers the label.
    occurrenceUuid: tab === "comments" ? modalData?.uuid : null,
    occurrenceDate: modalData?.date,
    // The row behind this modal shows a comment count; let it refresh.
    onPosted: props.onCommented,
    // The day's thread, always: for an hourly task a note left on the 11 AM
    // slot is seen by whoever opens the 3 PM one, and a day with one
    // occurrence comes back as itself. The backend judges the day, not the
    // task's current definition (an hourly task since made daily keeps
    // yesterday's slots).
    wholeDay: true,
    kioskParams: kiosk ? kioskParams : null,
  });
  const commentCount = Math.max(comments.length, modalData?.comment_count || 0);
  const canComment = !!(modalData?.store_id && modalData?.uuid);

  // Edit on a finished step (locked on a shared task) turns it back into
  // an input. `reopened` goes up with the save so the server takes the
  // change; every other finished step stays as the server has it.
  const reopenStep = (index) => {
    const updatedSubtasks = [...modalData.plan_subtask_assignments];
    updatedSubtasks[index] = { ...updatedSubtasks[index], reopened: true };
    updateDraft({ ...modalData, plan_subtask_assignments: updatedSubtasks });
  };

  // Progress saves stay in this editor. Only a successful final submission
  // closes it; a failure keeps the draft for another try.
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(null);
  const editedFieldsRef = React.useRef(new Map());
  const [saveFeedback, setSaveFeedback] = React.useState("");
  const updateDraft = (next) => {
    setSaveFeedback("");
    setModalData((current) => {
      const draft = typeof next === "function" ? next(current) : next;
      draft.plan_subtask_assignments?.forEach((row, index) => {
        const key = row.uuid ?? index;
        const previous = current.plan_subtask_assignments?.find((item, i) => (item.uuid ?? i) === key);
        const fields = editedFieldsRef.current.get(key) || new Set();
        for (const field of ["value_text", "value_boolean", "file_uploads", "file_upload", "photo_uuids", "reopened"]) {
          if (row[field] !== previous?.[field]) fields.add(field);
        }
        if (fields.size) editedFieldsRef.current.set(key, fields);
      });
      return draft;
    });
  };
  React.useEffect(() => {
    setModalData((current) => {
      if (!props.open || current?.uuid !== props.task?.uuid) {
        editedFieldsRef.current.clear();
        return props.task;
      }
      // Accept refreshed shared rows and metadata; retain only local response edits.
      return { ...props.task, plan_subtask_assignments: props.task?.plan_subtask_assignments?.map((row, index) => {
        const key = row.uuid ?? index;
        const fields = editedFieldsRef.current.get(key);
        const local = current.plan_subtask_assignments?.find((item, i) => (item.uuid ?? i) === key);
        if (!fields || !local) return row;
        const merged = { ...row };
        fields.forEach((field) => { merged[field] = local[field]; });
        return merged;
      }) };
    });
  }, [props.task, props.open]);
  React.useEffect(() => {
    savingRef.current = null;
    setSaving(false);
    setSaveFeedback("");
  }, [props.open, props.task?.uuid]);
  React.useEffect(() => {
    if (saveFeedback !== "Progress saved") return;
    const timer = setTimeout(() => setSaveFeedback(""), 2500);
    return () => clearTimeout(timer);
  }, [saveFeedback]);
  // Counts openings (and closes, and unmount). A slow save closes only the
  // opening it came from: the person may have closed the checklist and
  // opened another task, or this one again, meanwhile.
  const openingRef = React.useRef(0);
  React.useEffect(() => {
    openingRef.current += 1;
  }, [props.open, modalData?.uuid]);
  React.useEffect(() => () => {
    openingRef.current += 1;
  }, []);
  const handleSave = async (is_submit) => {
    if (savingRef.current) return;
    const opening = openingRef.current;
    const request = {};
    savingRef.current = request;
    setSaving(true);
    setSaveFeedback("");
    try {
      const ok = await props.save_plan_task_assignment(
        modalData.uuid,
        modalData.plan_subtask_assignments,
        is_submit
      );
      if (openingRef.current !== opening) return;
      if (ok === false) {
        setSaveFeedback("Could not confirm save. Your changes are still here. Try again.");
      } else if (is_submit) {
        props.handleClose();
      } else {
        // Retire only the fields confirmed by this save; a late upload may have
        // added a genuinely new draft value while the request was in flight.
        setModalData((current) => ({ ...current, plan_subtask_assignments: current.plan_subtask_assignments?.map((row, index) => {
          const key = row.uuid ?? index;
          const sent = modalData.plan_subtask_assignments?.find((item, i) => (item.uuid ?? i) === key);
          const fields = editedFieldsRef.current.get(key);
          if (!sent || !fields) return row;
          fields.forEach((field) => {
            if (field !== "reopened" && Object.is(row[field], sent[field])) fields.delete(field);
          });
          if ([...fields].some((field) => field !== "reopened")) return row;
          editedFieldsRef.current.delete(key);
          return row.reopened ? { ...row, reopened: false } : row;
        }) }));
        setSaveFeedback("Progress saved");
      }
    } catch {
      if (openingRef.current === opening) {
        setSaveFeedback("Could not confirm save. Your changes are still here. Try again.");
      }
    } finally {
      if (savingRef.current === request) {
        savingRef.current = null;
        setSaving(false);
      }
    }
  };

  // A long checklist (a 239-item audit) is hard to find your place in.
  // Reopening a started one lands on its first open required step. The step
  // is placed STEP_ANCHOR_PX under the top so the row above (often its
  // section heading) stays in view.
  const STEP_ANCHOR_PX = 72;
  const bodyRef = React.useRef(null);
  const stepTop = (index) => {
    const body = bodyRef.current;
    const el = body?.querySelector(`[data-step-index="${index}"]`);
    if (!body || !el) return null;
    return el.getBoundingClientRect().top - body.getBoundingClientRect().top;
  };
  const scrollToStep = (index, behavior) => {
    const body = bodyRef.current;
    const top = stepTop(index);
    if (top == null) return;
    body.scrollTo({ top: body.scrollTop + top - STEP_ANCHOR_PX, behavior });
  };
  // Once per opening, after the checklist has rendered.
  const resumedFor = React.useRef(null);
  React.useEffect(() => {
    if (!props.open) {
      resumedFor.current = null;
      return;
    }
    const uuid = modalData?.uuid;
    if (!uuid || tab !== "checklist" || resumedFor.current === uuid) return;
    resumedFor.current = uuid;
    const index = resumeStepIndex(modalData?.plan_subtask_assignments);
    if (index >= 0) requestAnimationFrame(() => scrollToStep(index, "auto"));
  }, [props.open, modalData?.uuid, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenPdfInNewTab = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  const requiredItems = (modalData?.plan_subtask_assignments || []).filter(
    (s) => !s.plan_subtask?.is_optional && s.plan_subtask?.type !== "heading"
  );
  const remainingCount = requiredItems.filter((s) => !locallyDone(s)).length;
  const allComplete = remainingCount === 0;
  const doneCount = requiredItems.length - remainingCount;
  const progressPct = requiredItems.length
    ? Math.round((doneCount / requiredItems.length) * 100)
    : 100;
  // Submitted work is read-only. The lifecycle status (and submitted_at) are
  // authoritative: the legacy five-string status can lag a submit by a round
  // trip, and a stale one would reopen the checklist for work already sent.
  const isSubmitted =
    modalData?.lifecycle_status === "completed" ||
    modalData?.lifecycle_status === "waiting_validation" ||
    !!modalData?.submitted_at ||
    modalData?.status === "completed" ||
    modalData?.status === "completed_late";

  // The backend refuses a submit before THIS occurrence opens: the slot's own
  // start for an intraday task, the task window otherwise. Mirror it, or the
  // button turns green early and the submit fails after the modal has closed.
  // The occurrence's own available_at is authoritative; the slot offset is the
  // fallback for a row that predates it.
  const slotIndex = modalData?.intraday_slot_index;
  const intervalMinutes =
    modalData?.plan_task?.intraday_repeat_interval_minutes || 0;
  const windowLowerQhnum = modalData?.plan_task?.lower_bound_qhnum;
  const slotLowerQhnum =
    windowLowerQhnum != null && slotIndex != null && intervalMinutes > 0
      ? windowLowerQhnum + (slotIndex * intervalMinutes) / 60
      : windowLowerQhnum;
  const windowOpensAt = modalData?.available_at
    ? new Date(modalData.available_at)
    : slotLowerQhnum != null && modalData?.date
    ? qhnumToDate(modalData.date, slotLowerQhnum)
    : null;
  const windowHasOpened = !windowOpensAt || new Date() >= windowOpensAt;
  const canSubmit = allComplete && windowHasOpened;

  // Time on task. Only a tracked task changes anything here: the checklist
  // opens with Start, offers Pause, and shows a clock. An untracked task
  // records its sessions silently server-side and the UI stays as it was.
  const tracked = !!modalData?.plan_task?.track_time;
  // my_session_running rides on Now rows and on every lifecycle response. A
  // day-payload occurrence (the manager drawer's "Work on it") lacks it, and
  // a missing value means "not running for me": the occurrence-wide
  // work_running may be a coworker's clock on a shared task, and unlocking on
  // it would let this person work without a session of their own. Pressing
  // Start on an already-running occurrence is tolerated server-side.
  const running = !!modalData?.my_session_running;
  const paused = !running && !!modalData?.paused;
  const needsStart = tracked && !isSubmitted && !running && !paused;
  const needsResume = tracked && !isSubmitted && paused;
  const inputsLocked = tracked && !isSubmitted && !running;
  const workRunning = !!modalData?.work_running || running;
  // Whose clock that is when it is not mine.
  const othersWorking = othersWorkingNames(modalData);

  // The clock counts from the moment this payload arrived. Only lifecycle
  // fields reset it; a checkbox tick must not send the clock backwards.
  const [fetchedAtMs, setFetchedAtMs] = React.useState(() => Date.now());
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = Date.now();
    setFetchedAtMs(t);
    setNowMs(t);
  }, [
    modalData?.uuid,
    modalData?.work_seconds,
    modalData?.work_running,
    modalData?.my_session_running,
  ]);
  // Tick while anyone is running (the clock) and while a tracked modal waits
  // for its window to open, so the disabled Start enables itself on time.
  const waitingForWindow = tracked && !isSubmitted && !windowHasOpened;
  React.useEffect(() => {
    if (!tracked || !(workRunning || waitingForWindow)) return undefined;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [tracked, workRunning, waitingForWindow]);
  // Lay a fresh Now row's lifecycle and work fields over the occurrence;
  // checklist edits stay local.
  const applyLifecycleRow = (next) => {
    setModalData((prev) => ({
      ...prev,
      paused: next.paused,
      work_seconds: next.work_seconds,
      work_running: next.work_running,
      work_running_count: next.work_running_count,
      work_open_member_ids: next.work_open_member_ids,
      work_idle_expires_at: next.work_idle_expires_at,
      work_open_expiries: next.work_open_expiries,
      work_open_sessions: next.work_open_sessions,
      work_estimated: next.work_estimated,
      my_session_running: next.my_session_running,
      assignees: next.assignees ?? prev.assignees,
      permissions: next.permissions,
      // The Now row calls the lifecycle status "status".
      lifecycle_status: next.lifecycle_status ?? next.status ?? prev.lifecycle_status,
      started_at: next.started_at,
    }));
  };

  // Once an open session's idle expiry passes, the server's sweep decides
  // the true value: fetch this occurrence's fresh Now row and lay its work
  // fields over the modal (the parents' copies refresh through onCommented).
  // The fresh row's expiries recompute `provisional`, which re-arms this if
  // the sweep has not run yet and the session is still open.
  const provisional = tracked && workClockIsProvisional(modalData, nowMs);
  const refetchedForExpiry = React.useRef(false);
  React.useEffect(() => {
    if (!provisional) {
      refetchedForExpiry.current = false;
      return undefined;
    }
    if (refetchedForExpiry.current) return undefined;
    refetchedForExpiry.current = true;
    let cancelled = false;
    const uuid = modalData?.uuid || modalData?.plan_task_assignment_uuid;
    const storeId = modalData?.store_id;
    if (uuid && storeId) {
      const nowUrl = kiosk ? API_get_now_tasks_kiosk : API_get_now_tasks;
      const nowQuery = new URLSearchParams({ store_id: storeId, include_done: "true", ...kioskParams }).toString();
      axios
        .get(`${nowUrl}?${nowQuery}`, getDefaultAxiosConfig())
        .then((res) => {
          if (cancelled) return;
          const payload = res.data?.redux?.payload || {};
          const rows = [...(payload.tasks || []), ...(payload.done_today || [])];
          const next = rows.find((r) => r.plan_task_assignment_uuid === uuid);
          if (next) applyLifecycleRow(next);
          // Give the sweep a chance to run before believing the guess again.
          setTimeout(() => {
            refetchedForExpiry.current = false;
          }, 5 * 60 * 1000);
        })
        .catch(() => {});
    }
    props.onCommented?.();
    return () => {
      cancelled = true;
    };
  }, [provisional]); // eslint-disable-line react-hooks/exhaustive-deps
  const workMs = workElapsedMs(
    {
      work_seconds: modalData?.work_seconds,
      work_running: workRunning,
      // The summed clock advances once per open session on a shared task,
      // and stops where the server's idle sweep could close it.
      work_running_count: modalData?.work_running_count,
      work_idle_expires_at: modalData?.work_idle_expires_at,
      work_open_expiries: modalData?.work_open_expiries,
      work_open_sessions: modalData?.work_open_sessions,
    },
    fetchedAtMs,
    nowMs
  );
  const showClock = tracked && (workRunning || (modalData?.work_seconds || 0) > 0);
  // Two or more people mid-session: a chip each, not one clock ticking twice as fast.
  const clocks = tracked ? memberClocks(modalData, fetchedAtMs, nowMs) : [];

  // Start / Pause / Resume post straight from here and stay open. Only the
  // lifecycle keys from the returned row are merged; checklist edits stay local.
  const [lifecycleBusy, setLifecycleBusy] = React.useState(false);
  const postLifecycle = async (kind) => {
    const uuid = modalData?.uuid || modalData?.plan_task_assignment_uuid;
    if (lifecycleBusy || !uuid) return;
    setLifecycleBusy(true);
    try {
      const res = await axios.post(
        lifecycleUrl(kind),
        {
          store_id: modalData.store_id,
          plan_task_assignment_uuid: uuid,
          specific_date: modalData.date,
          ...kioskParams,
        },
        getDefaultAxiosConfig()
      );
      const next = res.data?.redux?.payload?.plan_task_assignment;
      if (next) {
        applyLifecycleRow(next);
        // The list behind this modal holds its own copy of the row: hand it
        // the fresh one, and let the parents that only know how to refetch
        // do that (onCommented is their existing "refresh both lists" hook).
        props.onRowUpdated?.(next);
        props.onCommented?.();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message || "Could not update the task",
        { autoClose: 3000 }
      );
    } finally {
      setLifecycleBusy(false);
    }
  };

  return (
    <>
      <Modal
        open={props.open}
        onClose={() => props.handleClose()}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box ref={boxRef} sx={{ ...style, ...(lockedHeight ? { height: lockedHeight } : {}) }}>
          {/* Pinned header: title, meta, slot, and the live progress bar stay
              visible while the checklist scrolls beneath. */}
          <div className={`relative flex-none px-8 pt-5 space-y-3 ${canComment ? "" : "pb-4 border-b border-slate-200"}`}>
            <div
              className="absolute right-3 top-3 text-gray-500 cursor-pointer"
              onClick={() => props.handleClose()}
            >
              <CloseIcon />
            </div>
            <TaskNameCardModalHeader
              task={modalData?.plan_task}
              plan_task_assignment_members={
                modalData?.plan_task_assignment_members
              }
              handleOpenPdfInNewTab={handleOpenPdfInNewTab}
              setSelectedImage={setPreviewImage}
              setShowLightbox={setPreviewOpen}
              isAssignedToEveryone={props.isAssignedToEveryone}
              permissionType="employee"
              modalData={modalData}
              roleNames={props.roleNames}
            />
            {modalData?.intraday_slot_index != null && modalData?.plan_task && (
              <div className="bg-violet-50 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-violet-700">
                  Slot: {get_t_str(
                    (modalData.plan_task.lower_bound_qhnum || 0) +
                    modalData.intraday_slot_index * ((modalData.plan_task.intraday_repeat_interval_minutes || 60) / 60)
                  )} - {get_t_str(
                    (modalData.plan_task.lower_bound_qhnum || 0) +
                    modalData.intraday_slot_index * ((modalData.plan_task.intraday_repeat_interval_minutes || 60) / 60) +
                    ((modalData.plan_task.intraday_repeat_interval_minutes || 60) / 60)
                  )}
                </p>
              </div>
            )}
            {showClock && clocks.length > 1 ? (
              <div className="flex items-center gap-2 flex-wrap">
                {clocks.map((c) => (
                  <span
                    key={c.id}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums ${
                      c.running
                        ? "bg-violet-50 border-violet-200 text-violet-700"
                        : "bg-slate-50 border-slate-200 text-slate-600"
                    }`}
                  >
                    <TimerOutlinedIcon style={{ fontSize: 14 }} />
                    {provisional ? "~" : ""}{elapsedClock(c.ms)} · {c.name}
                  </span>
                ))}
              </div>
            ) : showClock && (
              <div className="flex items-center">
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums ${
                    workRunning
                      ? "bg-violet-50 border-violet-200 text-violet-700"
                      : "bg-slate-50 border-slate-200 text-slate-600"
                  }`}
                >
                  <TimerOutlinedIcon style={{ fontSize: 14 }} />
                  {workRunning
                    ? `${elapsedClock(workMs)} on task${othersWorking.length ? ` · ${namesPhrase(othersWorking)}` : ""}`
                    : `${durationLabel(workMs)} on task${paused ? " · paused" : ""}`}
                  {modalData?.work_estimated || provisional ? " (estimated)" : ""}
                </span>
              </div>
            )}
            {!isSubmitted && requiredItems.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="flex-1 h-2 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                  <span
                    className={`block h-full rounded-full transition-all ${
                      allComplete ? "bg-emerald-500" : "bg-violet-500"
                    }`}
                    style={{ width: `${progressPct}%` }}
                  />
                </span>
                <span className="text-xs font-semibold text-slate-500 tabular-nums whitespace-nowrap">
                  {doneCount}/{requiredItems.length} required
                </span>
              </div>
            )}
            {canComment && (
              <div className="flex border-b border-slate-200 -mx-8 px-8">
                {[
                  ["checklist", "Checklist"],
                  ["comments", "Comments"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      tab === key
                        ? "border-violet-700 text-violet-700"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                    onClick={() => switchTab(key)}
                  >
                    {label}
                    {key === "comments" && commentCount > 0 && (
                      <span
                        className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${
                          tab === key ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {commentCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scrolling body: the thread, or the checklist */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-8 py-5">
            {tab === "comments" ? (
              <CommentList comments={comments} loading={commentsLoading} />
            ) : (<>
            {!modalData?.plan_subtask_assignments?.length && (
              <p className="text-sm text-slate-400">
                This task has no checklist. Submit it once the work is done.
              </p>
            )}
            {modalData?.plan_subtask_assignments?.length > 0 && (
              <div>
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-1">
                  {modalData?.plan_subtask_assignments?.map(
                    (subtask, index) => (
                      <div
                        key={index}
                        data-step-index={index}
                      >
                        {(subtask.is_completed &&
                          !subtask.reopened &&
                          (props.isAssignedToEveryone ||
                            modalData?.plan_task_assignment_members?.length >
                              1)) ||
                        isSubmitted ? (
                          <div>
                            <TaskNameCardCompletedTask
                              isAssignedToEveryone={props.isAssignedToEveryone}
                              plan_task_assignment_members={
                                modalData?.plan_task_assignment_members
                              }
                              subtask={subtask}
                              index={index}
                              modalData={modalData}
                              setModalData={updateDraft}
                              homeCompany={props.homeCompany}
                              setShowLightbox={setPreviewOpen}
                              setSelectedImage={setPreviewImage}
                              onEdit={
                                isSubmitted || inputsLocked || saving
                                  ? undefined
                                  : () => reopenStep(index)
                              }
                            />
                          </div>
                        ) : (
                          <div>
                            <TaskNameCardInput
                              isAssignedToEveryone={props.isAssignedToEveryone}
                              subtask={subtask}
                              index={index}
                              modalData={modalData}
                              setModalData={updateDraft}
                              homeCompany={props.homeCompany}
                              setShowLightbox={setPreviewOpen}
                              setSelectedImage={setPreviewImage}
                              disabled={inputsLocked || saving}
                            />
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            </>)}
          </div>

          {/* Pinned footer: one adaptive action instead of Cancel / Save /
              Submit, always visible no matter how long the checklist is. The
              button saves progress until every required item is complete, then
              becomes the submit. Dismissal is the header close. */}
          {tab === "comments" ? (
            <div className="flex-none px-8 py-4 border-t border-slate-100">
              <CommentComposer onPost={post} posting={posting} placeholder="Ask a manager or leave a note..." />
            </div>
          ) : isSubmitted ? (
            <div className="flex-none px-8 py-4 border-t border-slate-100 text-right">
              <p className="text-xs text-slate-400">
                Submitted{modalData?.submitted_at_formatted ? ` · ${modalData.submitted_at_formatted}` : ""}. Nothing left to do here.
              </p>
            </div>
          ) : needsStart || needsResume ? (
            <div className="flex-none px-8 py-4 border-t border-slate-100 space-y-3">
              <p className="text-xs text-slate-400">
                {needsResume
                  ? "Paused. Resume to keep working."
                  : !windowHasOpened && windowOpensAt
                  ? `Opens at ${windowOpensAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}. Start becomes available then.`
                  : othersWorking.length
                  ? joinPrompt(othersWorking)
                  : "Press Start to begin. The clock runs until you submit."}
              </p>
              <button
                className="w-full px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 whitespace-nowrap bg-violet-700 hover:bg-violet-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                // No clock runs before the window opens (the server refuses
                // a Start there too), so the button waits with the window.
                disabled={lifecycleBusy || (needsStart && !windowHasOpened)}
                onClick={() => postLifecycle(needsResume ? "resume" : "start")}
              >
                {needsResume ? "Resume" : "Start task"}
              </button>
            </div>
          ) : (
            <div className="flex-none px-8 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <p role={saveFeedback.startsWith("Could not") ? "alert" : "status"} className="text-xs text-slate-500">
                {saveFeedback || (!modalData?.plan_subtask_assignments?.length
                  ? "Submit to mark this task complete."
                  : canSubmit
                  ? "Everything is checked off. Submit to finish."
                  : allComplete
                  ? "Everything is saved and ready. Submitting opens later."
                  : `${remainingCount} required item${remainingCount !== 1 ? "s" : ""} left. You can save and come back.`)}
              </p>
              <div className="flex items-center gap-2 flex-none">
                {tracked && running && (
                  <button
                    className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 whitespace-nowrap bg-white border border-slate-200 text-slate-600 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={lifecycleBusy || saving}
                    onClick={() => postLifecycle("pause")}
                  >
                    Pause
                  </button>
                )}
                <button
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                    canSubmit
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-violet-700 hover:bg-violet-800 text-white"
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  disabled={saving}
                  onClick={() => handleSave(canSubmit)}
                >
                  {saving
                    ? canSubmit ? "Submitting..." : "Saving..."
                    : canSubmit ? "Submit Task" : "Save Progress"}
                </button>
              </div>
            </div>
          )}
        </Box>
      </Modal>
      <TaskPhotoPreview src={props.open ? previewImage : null} onClose={() => setPreviewImage(null)} />
    </>
  );
}

export default TaskNameCardEmployeeModal;
